import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { useDocumentTitle } from '@/shared/lib/use-document-title';
import { NotFoundPage } from '@/widgets/not-found';
import { useBillingProfile } from '../../entities/billing-profile';
import { orderErrorMessage, orderSummary, useOrder, useOrderPayment } from '../../entities/order';
import { useOrgContext } from '../../entities/org';
import { useProject } from '../../entities/project';
import { PayOrderForm } from '../../features/order/pay-order';
import { cardPaymentsAvailable } from '../../shared/api/stripe';
import { useFormat } from '../../shared/i18n/format';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../shared/ui/breadcrumb';
import { Button } from '../../shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../shared/ui/card';
import { ErrorState } from '../../shared/ui/error-state';
import { Notice } from '../../shared/ui/notice';
import { PageLayout } from '../../shared/ui/page-layout';
import { Skeleton } from '../../shared/ui/skeleton';
import { StatusBadge } from '../../shared/ui/status-badge';
import { TopBarBreadcrumb } from '../../widgets/app-shell';

/** How long the page waits for the webhook before it says "this is taking longer than usual". */
const SLOW_AFTER_MS = 30_000;

/**
 * `/o/:orgSlug/orders/:orderId/pay`. The link is shareable: whoever placed the order can send it to
 * someone who holds `orders.pay`. Someone who can see the order but not pay it gets a clear refusal; someone who
 * cannot see it gets the standard not-found page. The page never marks anything paid: after the provider accepts
 * the card it polls the order until the webhook has placed it.
 */
export function OrderPayPage() {
  const { t } = useTranslation('order');
  useDocumentTitle(t('pay.documentTitle'));
  const { orderId = '' } = useParams();
  const { org, canIn, can, activeWorkspaces, archivedWorkspaces } = useOrgContext();
  const { formatCurrency, formatDate } = useFormat();
  const [confirming, setConfirming] = useState(false);
  const [slow, setSlow] = useState(false);
  const order = useOrder(orderId, { pollMs: confirming ? 2000 : false });
  const project = useProject(
    order.data?.workspace_id ?? '',
    order.data?.project_id ?? '',
    Boolean(order.data),
  );
  const mayPay = order.data ? canIn(order.data.workspace_id, 'orders.pay') : false;
  const awaiting = order.data?.status === 'awaiting_payment';
  const available = cardPaymentsAvailable();
  const payment = useOrderPayment(orderId, awaiting && mayPay && available && !confirming);
  // the billing profile prefills the address; only people with billing.view may read it, so ask only then
  const profile = useBillingProfile(org.id, can('billing.view') && awaiting && mayPay);

  useEffect(() => {
    if (!confirming) return;
    const timer = setTimeout(() => setSlow(true), SLOW_AFTER_MS);
    return () => clearTimeout(timer);
  }, [confirming]);

  if (order.isSuccess && (order.data === null || order.data.organization_id !== org.id))
    return <NotFoundPage />;

  const o = order.data;
  const workspace = o
    ? [...activeWorkspaces, ...archivedWorkspaces].find((w) => w.id === o.workspace_id)
    : undefined;
  const projectPath =
    o && workspace ? `/o/${org.slug}/w/${workspace.slug}/projects/${o.project_id}` : null;
  const summary = o ? orderSummary(t, o) : '';

  return (
    <PageLayout title={t('pay.title')} description={org.name} width="narrow">
      <TopBarBreadcrumb>
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={`/o/${org.slug}`}>{org.name}</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t('pay.documentTitle')}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </TopBarBreadcrumb>

      {order.isPending ? (
        <section aria-busy="true" aria-label={t('pay.loading')} className="grid gap-3">
          <Skeleton className="h-32 w-full" />
        </section>
      ) : null}
      {order.isError ? (
        <ErrorState
          title={t('pay.error')}
          description={order.error.message}
          onRetry={() => void order.refetch()}
        />
      ) : null}

      {o ? (
        <Card>
          <CardHeader>
            <CardTitle asChild className="font-sans">
              <h2>{t('pay.order')}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <dt className="text-sm text-muted-foreground">{t('pay.project')}</dt>
                <dd className="font-bold text-heading">
                  {/* someone who pays without projects.view sees the order, not the project's name */}
                  {project.data?.name ?? summary}
                </dd>
                {project.data ? <dd>{summary}</dd> : null}
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">{t('pay.amount')}</dt>
                <dd className="font-bold text-heading">
                  {o.amount_cents !== null && o.currency
                    ? formatCurrency(o.amount_cents, o.currency)
                    : t('pay.amountHidden')}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">{t('pay.order')}</dt>
                <dd>
                  <StatusBadge
                    tone={
                      o.status === 'placed'
                        ? 'success'
                        : o.status === 'awaiting_payment'
                          ? 'warning'
                          : 'neutral'
                    }
                  >
                    {t(`status.${o.status}`)}
                  </StatusBadge>
                </dd>
              </div>
              {o.reference ? (
                <div>
                  <dt className="text-sm text-muted-foreground">{t('pay.reference')}</dt>
                  <dd>{o.reference}</dd>
                </div>
              ) : null}
              {awaiting && o.expires_at ? (
                <div>
                  <dt className="text-sm text-muted-foreground">{t('pay.expires')}</dt>
                  <dd>{formatDate(o.expires_at)}</dd>
                </div>
              ) : null}
            </dl>
          </CardContent>
        </Card>
      ) : null}

      {o?.status === 'placed' ? (
        <Card>
          <CardHeader>
            <CardTitle asChild className="font-sans">
              <h2>{t('pay.placedTitle')}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            <p>{t('pay.placed')}</p>
            {projectPath ? (
              <div>
                <Button asChild>
                  <Link to={projectPath}>{t('pay.backToProject')}</Link>
                </Button>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {o && !awaiting && o.status !== 'placed' ? <Notice>{t('pay.notAwaiting')}</Notice> : null}

      {awaiting && !mayPay ? (
        <Notice tone="warning">
          <strong className="block text-heading">{t('pay.noPermissionTitle')}</strong>
          {t('pay.noPermission')}
        </Notice>
      ) : null}

      {awaiting && mayPay && !available ? (
        <Notice tone="warning">
          <strong className="block text-heading">{t('pay.unavailableTitle')}</strong>
          {t('pay.unavailable')}
        </Notice>
      ) : null}

      {awaiting && mayPay && available ? (
        <Card>
          <CardContent className="grid gap-4">
            {confirming ? (
              <p role="status" aria-live="polite">
                {slow ? t('pay.confirmSlow') : t('pay.confirming')}
              </p>
            ) : payment.isPending ? (
              <section aria-busy="true" aria-label={t('pay.starting')} className="grid gap-3">
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-24 w-full" />
              </section>
            ) : payment.isError ? (
              <ErrorState
                size="compact"
                title={t('pay.startError')}
                description={orderErrorMessage(payment.error)}
                onRetry={() => void payment.refetch()}
              />
            ) : (
              <PayOrderForm
                clientSecret={payment.data.clientSecret}
                billingAddress={
                  profile.data
                    ? {
                        name: profile.data.legal_name,
                        line1: profile.data.line1,
                        line2: profile.data.line2,
                        city: profile.data.city,
                        region: profile.data.region,
                        postalCode: profile.data.postal_code,
                        country: profile.data.country,
                      }
                    : null
                }
                cardSaveOffered={payment.data.cardSaveOffered}
                onPaid={() => setConfirming(true)}
              />
            )}
          </CardContent>
        </Card>
      ) : null}
    </PageLayout>
  );
}
